import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  Business,
  Code,
  Email,
  Phone,
  Language,
  LocationOn,
  Place,
  School,
  Person,
  FamilyRestroom
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../components/auth/AuthContext';

const SchoolProfile = () => {
  const { schoolId, schoolName } = useAuth();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'error' 
  });

  const fetchSchoolProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('https://kidsm.vercel.app/api/schools/profile', {
        params: { schoolId }
      });
      
      setSchool(response.data);
    } catch (err) {
      console.error('Error fetching school profile:', err);
      setError(err.response?.data?.message || 'Failed to load school profile');
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to load school profile',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  if (!schoolId) {
    setError('Authentication required');
    setLoading(false);
    setSnackbar({
      open: true,
      message: 'Please login to access this page',
      severity: 'error'
    });
    navigate('/login');
  } else {
    fetchSchoolProfile();
  }
}, [schoolId, navigate]);

  const handleRetry = () => {
    fetchSchoolProfile();
  };

  if (!schoolId) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ textAlign: 'center', color: '#e74c3c' }}>
          Error: No school ID provided
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress color="primary" />
        </Box>
      </Container>
    );
  }

  if (error || !school) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh' 
        }}>
          <Typography sx={{ 
            fontSize: '1rem', 
            color: '#e74c3c', 
            mb: 2, 
            textAlign: 'center' 
          }}>
            {error || 'School data not available'}
          </Typography>
          <Button
            variant="contained"
            onClick={handleRetry}
            sx={{ 
              bgcolor: '#6200ee', 
              borderRadius: 2, 
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#3700b3'
              }
            }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.5 }}
      >
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton 
            onClick={() => navigate(-1)} 
            sx={{ color: '#6200ee' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#2c3e50', 
              flexGrow: 1, 
              textAlign: 'center' 
            }}
          >
            {schoolName || 'School Profile'}
          </Typography>
          <Box sx={{ width: 48 }} />
        </Box>

        {/* School Avatar */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 150,
              height: 150,
              bgcolor: '#e0e0e0',
              '& .MuiSvgIcon-root': { 
                fontSize: 80, 
                color: '#6200ee' 
              },
            }}
          >
            <School />
          </Avatar>
        </Box>

        {/* Basic Information Card */}
        <Card sx={{ 
          bgcolor: '#fff', 
          borderRadius: 2, 
          p: 2, 
          mb: 3, 
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)' 
        }}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#6200ee', 
                mb: 2, 
                borderBottom: '1px solid #e0e0e0', 
                pb: 1 
              }}
            >
              Basic Information
            </Typography>
            
            <InfoItem icon={<Business />} text={school.schoolName} />
            <InfoItem icon={<Code />} text={school.schoolCode} />
            <InfoItem icon={<Email />} text={school.email} />
            <InfoItem icon={<Phone />} text={school.phoneNumber} />
            <InfoItem icon={<Language />} text={school.website || 'Not specified'} />
          </CardContent>
        </Card>

        {/* Location Card */}
        <Card sx={{ 
          bgcolor: '#fff', 
          borderRadius: 2, 
          p: 2, 
          mb: 3, 
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)' 
        }}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#6200ee', 
                mb: 2, 
                borderBottom: '1px solid #e0e0e0', 
                pb: 1 
              }}
            >
              Location
            </Typography>
            
            <InfoItem icon={<LocationOn />} text={school.address} />
            <InfoItem 
              icon={<Place />} 
              text={[school.village, school.location, school.subcounty, school.county]
                .filter(Boolean)
                .join(', ')}
            />
          </CardContent>
        </Card>

        {/* Statistics Card */}
        <Card sx={{ 
          bgcolor: '#fff', 
          borderRadius: 2, 
          p: 2, 
          mb: 3, 
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)' 
        }}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#6200ee', 
                mb: 2, 
                borderBottom: '1px solid #e0e0e0', 
                pb: 1 
              }}
            >
              Statistics
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <StatItem 
                icon={<School />} 
                value={school.statistics?.learners || 0} 
                label="Students" 
                color="#4CAF50" 
              />
              <StatItem 
                icon={<Person />} 
                value={school.statistics?.teachers || 0} 
                label="Teachers" 
                color="#2196F3" 
              />
              <StatItem 
                icon={<FamilyRestroom />} 
                value={school.statistics?.parents || 0} 
                label="Parents" 
                color="#FF9800" 
              />
            </Box>
          </CardContent>
        </Card>

        {/* Edit Profile Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate('/edit-school-profile', { state: { school } })}
          sx={{
            bgcolor: '#6200ee',
            borderRadius: 2,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 'bold',
            mt: 2,
            '&:hover': { 
              bgcolor: '#3700b3' 
            },
          }}
        >
          Edit Profile
        </Button>
      </motion.div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Reusable InfoItem component
const InfoItem = ({ icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
    {React.cloneElement(icon, { 
      sx: { 
        color: '#6200ee', 
        mr: 1, 
        fontSize: 20 
      } 
    })}
    <Typography sx={{ fontSize: '1rem', color: '#333' }}>
      {text}
    </Typography>
  </Box>
);

// Reusable StatItem component
const StatItem = ({ icon, value, label, color }) => (
  <Box sx={{ textAlign: 'center', flex: 1 }}>
    {React.cloneElement(icon, { 
      sx: { 
        fontSize: 30, 
        color: color 
      } 
    })}
    <Typography sx={{ 
      fontSize: '1.5rem', 
      fontWeight: 'bold', 
      color: '#2c3e50', 
      my: 0.5 
    }}>
      {value}
    </Typography>
    <Typography sx={{ 
      fontSize: '0.875rem', 
      color: '#7f8c8d' 
    }}>
      {label}
    </Typography>
  </Box>
);

export default SchoolProfile;